<template>
  <div
    class="price-info"
    :class="{
      'price-info--reduction': value.old,
      [`price-info--currency-${value.currency}`]: true
    }"
    :aria-label="t('checkout.basket.position.header.price') + ' ' + value.current"
  >
    <s
      v-if="value.old"
      class="price-info__old"
    >
      <span class="price-info__old-value">
        {{ value.old }}
      </span>
    </s>

    <div
      class="price-info__current"
      :aria-current="value.old ? 'true' : null"
    >
      <span class="price-info__value">
        <span class="price-info__value-euro">{{ splitCurrentValue[0] }}{{ priceDot }}</span>
        <small class="price-info__value-cent">{{ splitCurrentValue[1] }}</small>
      </span>
      <span class="price-info__currency">
        {{ value.currency }}
      </span>
      <sup
        v-if="showStar"
        class="price-info__asterisk"
      >
        *
      </sup>
      <span
        class="price-info__savedPercentage"
        v-if="showSavedPercentage && value.savedPercentage"
      >
        -{{ value.savedPercentage }}%
      </span>
    </div>
    <div
      class="price-info__info"
      :class="{
        'price-info__info--inline': foodSpecificationLink
      }"
    >
      <a
        v-if="foodSpecificationLink"
        class="price-info__food-specification-link"
        :href="foodSpecificationLink"
        v-html="t('detail.product.characteristics')"
      />
      <div class="price-info__unit">
        <span class="price-info__unit-text">
          {{ getUnitText() }}
        </span>
        <span
          v-if="value.unitPrice"
          class="price-info__unit-price"
        >
          ({{ value.unitPrice }}<template v-if="value.unit">/{{ value.unit }}</template>)
        </span>
      </div>
    </div>
    <div
      v-if="showTax || articleNumber.length > 0"
      class="price-info__tax-wrapper"
    >
      <div
        v-if="showTax"
        class="price-info__tax"
        v-html="t(showShippingIncluded ? messageKey.deliveryInclShip : messageKey.deliveryInclVat, [shippingInfoUrl])"
      />
      <div
        v-if="articleNumber.length > 0"
        class="price-info__tax--article-number"
      >
        {{ t('checkout.basket.position.productNr') }} {{ articleNumber }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {computed, ref} from 'vue';
  import type Price from '../../../bps/types/Price';
  import {t} from '@/bps/utilities/js/translations/translations';

  const props = withDefaults(defineProps<{
    value: Price;
    showStar?: boolean;
    showTax?: boolean;
    showShippingIncluded?: boolean;
    showSavedPercentage?: boolean;
    shippingInfoUrl?: string;
    foodSpecificationLink?: string;
    articleNumber?: string;
    showVolumeUnitText?: boolean;
  }>(), {
    showStar: false,
    showTax: false,
    showShippingIncluded: false,
    showSavedPercentage: false,
    shippingInfoUrl: '',
    foodSpecificationLink: '',
    articleNumber: '',
    showVolumeUnitText: false
  });

  const messageKey = ref<Record<string, string>>({
    deliveryInclShip: 'price.incl_vat_incl_ship',
    deliveryInclVat: 'price.incl_vat',
  });
  if (window.__productConfig && window.__productConfig.messageKey) {
    messageKey.value = window.__productConfig.messageKey;
  }

  const getUnitText = () => {
    let result = props.showVolumeUnitText
      ? `${t(`product.price.volumeUnit.${props.value.volumeUnit}`)} `
      : '';
    if (props.value.unitPrice) {
      if (props.value.packageSize) {
        result += props.value.packageSize
          .replace('.', ',')
          .replace('l', ' L');
      }
    }
    return result;
  };

  const splitCurrentValue = computed(() => {
    if (props.value.currency.toUpperCase() === 'CHF') {
      return props.value.currentValue
        .replace(props.value.currency, '')
        .split(/[:.]/);
    }
    return props.value.currentValue.split(/[:,]/);
  });

  const priceDot = props.value.currency.toUpperCase() === 'CHF' ? '.' : ',';
</script>
